import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Trash2, RefreshCw, Plus, Wand2, AlertTriangle } from 'lucide-react';
import {
  fetchSedeCodeCorrections, createSedeCodeCorrection, deleteSedeCodeCorrection, suggestSedeCodePad,
} from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

export function SedeCodeCorrectionsTab() {
  const qc = useQueryClient();
  const [alias, setAlias] = useState('');
  const [canonical, setCanonical] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sede-code-corrections'],
    queryFn: fetchSedeCodeCorrections,
  });
  const items = data?.items || [];
  const dirtyTargets = new Set(data?.dirty_targets || []);

  const handleSuggest = async () => {
    if (!/^\d{6}$/.test(alias.trim())) {
      toast.info('La sugerencia automática solo aplica a códigos numéricos de 6 dígitos (truncados)');
      return;
    }
    setSuggesting(true);
    try {
      const res = await suggestSedeCodePad(alias.trim());
      if (!res.suggestions?.length) { toast.warning('Sin coincidencias — introduce el código destino manualmente'); return; }
      const best = res.suggestions[0];
      setSuggestion(best);
      setCanonical(best.codigo);
      setLabel(best.nombre || '');
      toast.success(`Sugerido: ${best.codigo} — ${best.nombre}`);
    } catch { toast.error('Error al buscar sugerencia'); }
    finally { setSuggesting(false); }
  };

  const handleCreate = async () => {
    if (!alias.trim() || !canonical.trim()) { toast.error('Código origen y destino son requeridos'); return; }
    setSaving(true);
    try {
      const res = await createSedeCodeCorrection({
        alias_code: alias.trim(), canonical_code: canonical.trim(), canonical_label: label.trim(),
      });
      if (res.warning) toast.warning(res.warning);
      toast.success(`"${alias}" → "${canonical}" · ${res.updated} registros actualizados`);
      setAlias(''); setCanonical(''); setLabel(''); setSuggestion(null);
      qc.invalidateQueries({ queryKey: ['sede-code-corrections'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar la corrección');
    } finally { setSaving(false); }
  };

  const handleDelete = async (aliasCode) => {
    await deleteSedeCodeCorrection(aliasCode);
    toast.success('Corrección eliminada');
    qc.invalidateQueries({ queryKey: ['sede-code-corrections'] });
  };

  return (
    <div className="space-y-5" data-testid="sede-code-corrections-tab">
      <div>
        <h2 className="text-base font-semibold" style={{ color: 'var(--vz-text-primary)' }}>Código → Código</h2>
        <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
          Fusiona un código de sede duplicado/truncado (origen) en el código canónico (destino). Se aplica
          de inmediato a OPS, Liderazgo y Near Misses.
        </p>
      </div>

      <div className="rounded-xl border p-4 flex flex-wrap items-end gap-3" style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-elevated)' }}>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium" style={{ color: 'var(--vz-text-muted)' }}>Código origen (alias)</label>
          <Input className="h-8 text-xs font-mono w-40" placeholder="ej. 500885" value={alias}
            onChange={e => setAlias(e.target.value)} data-testid="code-corr-alias-input" />
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={handleSuggest}
          disabled={suggesting || !alias.trim()} data-testid="code-corr-suggest-btn">
          {suggesting ? <RefreshCw size={12} className="animate-spin" /> : <Wand2 size={12} />} Auto-detectar
        </Button>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium" style={{ color: 'var(--vz-text-muted)' }}>Código destino (canónico)</label>
          <Input className="h-8 text-xs font-mono w-40" placeholder="ej. 5000885" value={canonical}
            onChange={e => setCanonical(e.target.value)} data-testid="code-corr-canonical-input" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium" style={{ color: 'var(--vz-text-muted)' }}>Etiqueta (opcional)</label>
          <Input className="h-8 text-xs w-52" placeholder="Nombre legible" value={label}
            onChange={e => setLabel(e.target.value)} data-testid="code-corr-label-input" />
        </div>
        {canonical && dirtyTargets.has(canonical.trim()) && (
          <span className="flex items-center gap-1 text-xs" style={{ color: '#F59E0B' }}>
            <AlertTriangle size={12} /> Código sucio — no recomendado como destino
          </span>
        )}
        <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={handleCreate} disabled={saving}
          data-testid="code-corr-save-btn">
          {saving ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />} Crear corrección
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-xs" style={{ color: 'var(--vz-text-muted)' }}>
          No hay correcciones código→código guardadas todavía
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--vz-bg-elevated)' }}>
                <TableHead className="text-xs">Origen</TableHead>
                <TableHead className="text-xs">Destino</TableHead>
                <TableHead className="text-xs">Etiqueta</TableHead>
                <TableHead className="text-xs">Origen del dato</TableHead>
                <TableHead className="text-xs w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(it => (
                <TableRow key={it.alias_code} className="text-xs" data-testid={`code-corr-row-${it.alias_code}`}>
                  <TableCell><code className="font-mono" style={{ color: 'var(--vz-text-muted)' }}>{it.alias_code}</code></TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#4A9EFF' }}>
                      <Link2 size={11} /> {it.canonical_code}
                    </span>
                  </TableCell>
                  <TableCell style={{ color: 'var(--vz-text-secondary)' }}>{it.canonical_label || '—'}</TableCell>
                  <TableCell style={{ color: 'var(--vz-text-muted)' }}>{it.source === 'admin_ui' ? 'Manual' : 'Config'}</TableCell>
                  <TableCell>
                    <button onClick={() => handleDelete(it.alias_code)} className="opacity-50 hover:opacity-100 transition-opacity" data-testid={`code-corr-delete-${it.alias_code}`}>
                      <Trash2 size={13} style={{ color: '#EF4444' }} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
